import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useSocket } from './SocketContext';

export interface LiveSessionEvent {
  type: 'session:opened' | 'session:set-completed' | 'session:exercise-completed' | 'session:completed';
  clientId: number;
  clientName?: string;
  workoutInstanceId?: number;
  planName?: string;
  exerciseInstanceId?: number;
  exerciseName?: string;
  setNumber?: number;
  totalSets?: number;
  exerciseIndex?: number;
  totalExercises?: number;
  at: string;
}

export interface LiveSession {
  clientId: number;
  clientName?: string;
  workoutInstanceId?: number;
  planName?: string;
  status: 'live' | 'completed';
  exerciseName?: string;
  setNumber?: number;
  totalSets?: number;
  exerciseIndex?: number;
  totalExercises?: number;
  startedAt: string;
  lastActionAt: string;
}

interface LiveCoachFeedValue {
  liveSessions: LiveSession[];
  connected: boolean;
}

const Ctx = createContext<LiveCoachFeedValue>({ liveSessions: [], connected: false });

const EVENTS: LiveSessionEvent['type'][] = [
  'session:opened',
  'session:set-completed',
  'session:exercise-completed',
  'session:completed'
];

/**
 * Holds the live coach feed at the app-root level so it survives when the
 * coach navigates between pages. Before this lived inside the widget itself,
 * which meant nav away + back rebuilt an empty Map and waited for the next
 * client action to repopulate — even though the socket (in SocketContext)
 * never disconnected.
 *
 * The provider stays mounted as long as the user session lives. On socket
 * reconnect the backend replays its snapshot, so state stays consistent
 * across full page reloads too.
 */
export function LiveCoachFeedProvider({ children }: { children: ReactNode }) {
  const { socket, connected } = useSocket();
  const [sessions, setSessions] = useState<Map<number, LiveSession>>(new Map());

  useEffect(() => {
    if (!socket) return;
    const handler = (event: LiveSessionEvent) => {
      setSessions((prev) => {
        const next = new Map(prev);
        const existing = next.get(event.clientId);
        if (event.type === 'session:completed') {
          next.set(event.clientId, {
            ...(existing ?? { clientId: event.clientId, startedAt: event.at }),
            clientName: event.clientName ?? existing?.clientName,
            workoutInstanceId: event.workoutInstanceId ?? existing?.workoutInstanceId,
            planName: event.planName ?? existing?.planName,
            status: 'completed',
            lastActionAt: event.at
          });
          return next;
        }
        next.set(event.clientId, {
          clientId: event.clientId,
          clientName: event.clientName ?? existing?.clientName,
          workoutInstanceId: event.workoutInstanceId ?? existing?.workoutInstanceId,
          planName: event.planName ?? existing?.planName,
          exerciseName: event.exerciseName ?? existing?.exerciseName,
          setNumber: event.setNumber ?? existing?.setNumber,
          totalSets: event.totalSets ?? existing?.totalSets,
          exerciseIndex: event.exerciseIndex ?? existing?.exerciseIndex,
          totalExercises: event.totalExercises ?? existing?.totalExercises,
          status: 'live',
          startedAt: existing?.startedAt ?? event.at,
          lastActionAt: event.at
        });
        return next;
      });
    };

    EVENTS.forEach((e) => socket.on(e, handler));
    return () => {
      EVENTS.forEach((e) => socket.off(e, handler));
    };
  }, [socket]);

  // Sweep stale + completed entries every 30s.
  useEffect(() => {
    const id = setInterval(() => {
      const cutoff = Date.now() - 15 * 60 * 1000;
      setSessions((prev) => {
        let changed = false;
        const next = new Map(prev);
        prev.forEach((s, k) => {
          if (s.status === 'completed' || new Date(s.lastActionAt).getTime() < cutoff) {
            next.delete(k);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 30 * 1000);
    return () => clearInterval(id);
  }, []);

  const value = useMemo<LiveCoachFeedValue>(() => {
    const liveSessions = Array.from(sessions.values()).sort(
      (a, b) => new Date(b.lastActionAt).getTime() - new Date(a.lastActionAt).getTime()
    );
    return { liveSessions, connected };
  }, [sessions, connected]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLiveCoachFeed(): LiveCoachFeedValue {
  return useContext(Ctx);
}
